/**
 * QRLabelSheet — a printable sheet of field QR labels for plants/fruit or blocks.
 * Each QR deep-links to the in-app quick-record sheet (recordLink → /r/plant|block/:id,
 * login-gated). A worker scans a printed sticker/sign in the orchard to record picking
 * or other activity against that plant or block.
 *
 * QR images are generated client-side (qrcode lib → data URL); printing uses the
 * .qr-print-area print scope in design-system.css (everything else is hidden on print).
 */
import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { X, Printer } from 'lucide-react'

export interface QRLabelItem { url: string; title: string; subtitle?: string }

export function QRLabelSheet({ heading, action, items, onClose }: {
  heading: string
  action: string // small caption under each title, e.g. "Scan to record picking"
  items: QRLabelItem[]
  onClose: () => void
}) {
  const [imgs, setImgs] = useState<Record<string, string>>({})
  useEffect(() => {
    let cancelled = false
    Promise.all(items.map(async it =>
      [it.url, await QRCode.toDataURL(it.url, { margin: 1, width: 360, errorCorrectionLevel: 'M' })] as const,
    )).then(pairs => { if (!cancelled) setImgs(Object.fromEntries(pairs)) }).catch(() => {})
    return () => { cancelled = true }
  }, [items])

  const ready = items.length > 0 && items.every(it => imgs[it.url])

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div style={{ background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 14, width: 720, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Toolbar (not printed) */}
        <div className="qr-noprint" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--color-border)' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--color-text)' }}>{heading}</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--color-text-muted)' }}>{items.length} label{items.length === 1 ? '' : 's'} · print on sticker paper or plain paper for a sign</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => window.print()} disabled={!ready}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: 'var(--color-primary)', border: 'none', color: '#0E1209', cursor: ready ? 'pointer' : 'wait' }}>
              <Printer size={15} /> {ready ? 'Print' : 'Rendering…'}
            </button>
            <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)' }}><X size={18} /></button>
          </div>
        </div>

        {/* Print area — the only thing visible when printing */}
        <div className="qr-print-area" style={{ padding: 16, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {items.map(it => (
              <div key={it.url} className="qr-label-card" style={{ border: '1px solid #ccc', borderRadius: 10, padding: 12, textAlign: 'center', background: '#fff', color: '#111', breakInside: 'avoid' }}>
                {imgs[it.url]
                  ? <img src={imgs[it.url]} alt={`QR for ${it.title}`} style={{ width: '100%', maxWidth: 150, height: 'auto', margin: '0 auto', display: 'block' }} />
                  : <div style={{ width: 150, height: 150, margin: '0 auto', background: '#f0f0f0', borderRadius: 8 }} />}
                <div style={{ marginTop: 8, fontSize: 14, fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}>{it.title}</div>
                {it.subtitle && <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{it.subtitle}</div>}
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>{action}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
